import {all, call, fork, put, select, takeLatest} from 'redux-saga/effects'
import {get, isNil} from 'lodash'
import DataHelper from '../helpers/dataStructure'
import * as FieldsHelper from '../helpers/fields'
import * as ListHelper from '../helpers/contactLists'
import {createPivotTableWatcher} from './pivotTable'
import types from '../actions/types'
import * as listActions from '../actions/contactLists'
import * as pivotTableActions from '../actions/pivotTable'
import * as snackbarActions from '../actions/snackbar'
import {generalError} from '../actions/index'
import {
  allocateContactListFailure,
  allocateContactListSuccess,
  updateContactHistoryStats,
} from 'actions/contactLists'
import {loadContactListCategories, loadContactLists} from 'actions/data'
import {listBuilderContactsPivotTableKey} from 'reducers/contactsContext/listBuilder'
import {push} from 'react-router-redux'
import {urls} from 'helpers/url'
import iconNames from 'theme/icons'
import {getAllRows} from 'selectors/pivotTable'
import * as contactActions from 'actions/contacts'
import {companyInbound, contactInbound} from 'helpers/api'
import {saveContactChangesToApi} from 'sagas/contactSaga'
import {t} from "helpers/language"

const contactFields = {
  default: true,
  company: {
    default: true,
    accountingcompany: true,
    locations: true,
    products: true,
    groups: true,
    referralcompany: {
      name: true,
    },
    contacts: {
      id: true,
      companyId: true,
      fullName: true,
    },
    userfields: true,
    createdBy: true,
    modifiedBy: true,
  },
  location: true,
  userfields: true,
  accountmanager: true,
  campaign: true,
  contactmethods: true,
  historystat: {
    totalCalls: true,
    totalEmails: true,
    totalMeetings: true,
    totalNotes: true,
    totalQuoteFollowUps: true,
    totalTasks: true,
    totalToDos: true,
    totalRejectedActivities: true,
    totalCompletedCalls: true,
    totalPendingMeetings: true,
  },
  latestPendingCall: true,
  latestPendingMeeting: true,
}

export default (api) => {

  function* addCategory({parentId, name, forward}) {
    try {
      const newCategory = {
        name,
        parentId,
      }

      const response = yield call(api.contactListCategory.add, newCategory)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const category = data.result
            yield put(listActions.addCategorySuccess(category))
            yield put(snackbarActions.snackbarMessage(
                `${category.name} has been added to your database`, iconNames.tick
            ))

            if (forward) {
              yield put(listActions.selectCategory(category.id))
              yield put(push(urls.contacts.contactListsUrl))
            }
          },
          function* onFail(key, message, code) {
            yield put(listActions.addCategoryFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* updateCategory({category}) {
    try {
      const response = yield call(api.contactListCategory.edit, category)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const category = data.result
            yield put(listActions.updateCategorySuccess(category))
            yield put(snackbarActions.snackbarMessage(t('{1} has been updated', category.name), iconNames.tick))
          },
          function* onFail(key, message, code) {
            yield put(listActions.updateCategoryFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteCategories({categories, targetedCategory, adoptCategoryId}) {
    try {
      const response = yield call(api.contactListCategory.delete, categories, adoptCategoryId)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(listActions.deleteCategoriesSuccess(categories, adoptCategoryId))

            let message
            if (targetedCategory) {
              message = `${targetedCategory.name}`
              if (categories.length > 1) {
                message += ` and ${categories.length - 1} other folder(s) have been deleted`
              } else {
                message += ' has been deleted'
              }
            } else {
              message = `${categories.length} folder(s) have been deleted`
            }
            yield put(snackbarActions.snackbarMessage(message, iconNames.tick))
          },
          function* onFail(key, message, code) {
            yield put(listActions.deleteCategoriesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* getTotal() {
    try {
      const response = yield call(api.contact.summary, {}, {})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(listActions.getTotalSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(listActions.getTotalFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* refreshCounts({forceUpdate}) {
    try {
      // Establish list of contact lists to re-query
      const contactLists = yield select(state => state.contactsContext.contactLists)
      const {selectedCategoryId, showingHiddenLists} = contactLists
      let {search} = contactLists
      let lists = yield select(state => state.data.contactLists || [])

      // Filter to the displayed contact lists
      if (search && search.length > 0) {
        search = search.toLowerCase()
        lists = lists.filter(list => list.name.toLowerCase().indexOf(search) >= 0)
      } else if (selectedCategoryId) {
        lists = DataHelper.getAllBranchesForBranch(lists, selectedCategoryId, 'id', 'categoryId')
      }

      if (!showingHiddenLists) lists = lists.filter(cl => cl.active)

      // Filter out those that are already loading, and those that already have counts (unless we are force updating)
      lists = lists.filter(cl => !cl.loading)
      if (!forceUpdate) lists = lists.filter(cl => cl.summary === null)

      // Re-query the lists
      if (lists.length > 0) yield put(listActions.requeryLists(lists.map(cl => cl.id)))
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* requeryLists({listIds}) {
    try {
      while (listIds.length > 0) {
        const subLists = listIds.splice(0, 10)
        const response = yield call(api.contactList.requery, subLists)

        yield api.handleResponse(
            response,
            function* onOk(key, data) {
              yield put(listActions.requeryListsSuccess(subLists, data.result))
            },
            function* onFail(key, message, code) {
              yield put(listActions.requeryListsFailure(subLists, message, code))
            }
        )
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  const prepareListForSave = (list) => {
    let {contacts} = list
    if (contacts) contacts = contacts.map(c =>
        ({contactId: c.contactId, include: c.include}))  // remove contact reference
    return {
      ...list,
      contacts,
    }
  }

  function* addList({list, callback}) {
    try {
      const contactList = prepareListForSave(list)
      const fields = {
        default: true,
        contacts: true,
      }

      const response = yield call(api.contactList.add, contactList, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const list = data.result[0]
            yield put(listActions.addListSuccess(list))
            yield put(snackbarActions.snackbarMessage(`${list.name} added to your contact lists`, iconNames.tick))
            if (callback) callback(list)
          },
          function* onFail(key, message, code) {
            yield put(listActions.addListFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* updateList({list, callback}) {
    try {
      const {wasActive} = list

      const contactList = prepareListForSave(list)
      const fields = {
        default: true,
        contacts: true,
      }

      const response = yield call(api.contactList.edit, contactList, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const list = data.result[0]
            yield put(listActions.updateListSuccess(list))
            if (wasActive && !list.active) {
              const showingHiddenLists = yield select(state => state.contactsContext.contactLists.showingHiddenLists)
              if (!showingHiddenLists) {
                yield put(snackbarActions.snackbarMessage(
                    t('Your list is now hidden. To see it again, tick Show Hidden Lists'), null, false, true
                ))
              }
            } else {
              yield put(snackbarActions.snackbarMessage(t('{1} has been updated', list.name), iconNames.tick))
            }
            if (callback) callback(list)
          },
          function* onFail(key, message, code) {
            yield put(listActions.updateListFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteList({list}) {
    try {
      const response = yield call(api.contactList.delete, list.id)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(listActions.deleteListSuccess(list, data))
            yield put(snackbarActions.snackbarMessage(
                t('{1} removed from your contact lists', list.name), iconNames.tick
            ))
          },
          function* onFail(key, message, code) {
            yield put(listActions.deleteListFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* dormantContacts({list}) {
    try {
      const response = yield call(api.contactList.dormantContacts, list.id)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(listActions.dormantContactsSuccess(list, data.result))
            yield put(snackbarActions.snackbarMessage(
                `${data.result} contacts have been dormanted`, iconNames.tick
            ))
          },
          function* onFail(key, message, code) {
            yield put(listActions.dormantContactsFailure(list, message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* newBuilderList() {
    try {
      yield put(pivotTableActions.reset(listBuilderContactsPivotTableKey))
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadContactList({listId, createCopy, loadContacts}) {
    try {
      yield put(pivotTableActions.reset(listBuilderContactsPivotTableKey))

      const response = yield call(
          api.contactList.getOne,
          listId,
          {
            default: true,
            filters: true,
            lists: true,
            contacts: {
              default: true,
              contact: {
                default: true,
                company: {
                  name: true,
                  type: true,
                },
              },
            },
          }
      )

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            let list = data.result
            if (createCopy) list = {...list, name: `${list.name} (copy)`, id: null}
            yield put(listActions.loadContactListSuccess(list))
            if (loadContacts) yield put(pivotTableActions.requestData(listBuilderContactsPivotTableKey))
          },
          function* onFail(key, message, code) {
            yield put(listActions.loadContactListFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* reloadContactListCategories() {
    yield put(loadContactListCategories())
  }


  function* builderListUpdated({list, requery}) {
    try {
      if (requery) yield put(listActions.builderRequeryList(list))
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* builderCreateFilter({key}) {
    const {contactFields, companyFields, countries} = yield select(state => state.data)

    const allFields = FieldsHelper.getAllFields(contactFields, companyFields, true, countries)
    const otherFilters = yield select(state => get(state, 'contactsContext.listBuilder.selected.filters', []))
    const newFilter = ListHelper.createNewFilter(key, allFields, otherFilters)

    yield put(listActions.addBuilderFilter(newFilter))
  }

  function* builderCreateSubFilter({key}) {
    if (key && key.length > 0) {
      const type = key.charAt(0)
      const otherFilters = yield select(state => get(state, 'contactsContext.listBuilder.selected.filters', []))
      const updatedFilters = []

      // Add this sub-filter to all existing applicable filters (unless already present)
      otherFilters.forEach(filter => {
        if (filter.key && filter.key.length > 0 && filter.key.charAt(0) === type) {

          // Add a new sub-filter into this filter
          const {value, subFilters} = ListHelper.extractFilterParts(filter.value)
          if (!subFilters.some(f => f.key === key)) {

            updatedFilters.push({
              ...filter,
              value: ListHelper.encodeFilterParts(value, [
                ...subFilters,
                ListHelper.createNewSubFilter(key),
              ]),
            })
          }
        }
      })

      yield put(listActions.updateBuilderFilters(updatedFilters))
    }
  }

  function* builderFiltersChanged() {
    try {
      const list = yield select(state => state.contactsContext.listBuilder.selected)
      if (list) yield put(listActions.builderRequeryList(list))
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* builderRequeryList({list}) {
    try {
      const response = yield call(api.contactList.requeryList, list)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(listActions.builderRequeryListSuccess(list, data.result))
          },
          function* onFail(key, message, code) {
            yield put(listActions.builderRequeryListFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadFilterValues({key}) {
    try {
      const response = yield call(api.fieldsOptions.get, {key})
      yield api.handleResponse(
          response,
          function* onOk(k, data) {
            const singleKeyResult = data.result.length > 0 ? data.result[0] : null
            const values = singleKeyResult ? singleKeyResult.values : null
            if (values) {
              yield put(listActions.loadFilterValuesSuccess(key, values))
            } else {
              console.error('No values returned from API for field: ' + key)
            }
          },
          function* onFail(k, message, code) {
            yield put(listActions.loadFilterValuesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* allocateContactList({name, contactListId, selectedContactIds}) {
    try {
      const params = {
        name,
        contactListId,
        selectedContactIds,
      }
      const fields = {}
      const response = yield call(api.contactList.allocate, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(allocateContactListSuccess(data))
            yield put(loadContactLists())
            yield put(snackbarActions.snackbarMessage(
                `Your contact list has been allocated to ${selectedContactIds.length} user(s)`, iconNames.tick
            ))
          },
          function* onFail(k, message, code) {
            yield put(allocateContactListFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* actionFailure({message, error}) {
    try {
      yield put(snackbarActions.snackbarMessage(message || error, 'error'))
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* updateContactHistoryStatForActivity({data}) {
    const activity = data[0]
    yield put(updateContactHistoryStats(activity))
  }

  function* updateContactHistoryStatForContact({data, contactId}) {
    if (isNil(contactId))
      contactId = data.id

    yield put(updateContactHistoryStats({contactId}))
  }

  function* updateContactHistoryStatForCompany({data, companyId}) {
    if (isNil(companyId)) companyId = data.id
    const contactListId = yield select(state => state.contactsContext.contactListView.contactListId)
    if (!isNil(contactListId)) {
      const state = yield select(state => state)
      const contacts = getAllRows(state, listBuilderContactsPivotTableKey)
          .filter(contact => get(contact, 'companyId') === companyId)
      for (const index in contacts) {
        const contactData = contacts[index]
        yield put(updateContactHistoryStats({contactId: contactData.id}))
      }
    }
  }

  function* updateHistoryStats({data}) {
    const contactListId = yield select(state => state.contactsContext.contactListView.contactListId)
    if (!isNil(contactListId)) {
      const contactId = get(data, 'contactId', null)
      if (!isNil(contactId)) {
        const state = yield select(state => state)
        const contacts = getAllRows(state, listBuilderContactsPivotTableKey)
        const contactToUpdate = contacts.find(contact => contact.id === contactId)
        if (!isNil(contactToUpdate)) {
          try {
            const response = yield call(api.contact.getOne, contactId, contactFields)
            yield api.handleResponse(
                response,
                function* onOk(key, data) {
                  let contact = data.result
                  contact = contactInbound(contact)
                  contact.company = companyInbound(contact.company)
                  yield put(pivotTableActions.editData(listBuilderContactsPivotTableKey, contact.id, contact, false))
                },
                function* onFail(key, message, code) {
                  yield put(contactActions.loadContactFailure(message, code))
                }
            )
          } catch (e) {
            console.error(e)
            yield put(generalError())
          }
        }
      }
    }
  }

  function* saveContactChanges(data) {
    return yield call(saveContactChangesToApi, data, api)
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.CONTACT_LISTS_ADD_CATEGORY_ATTEMPT, addCategory),
      takeLatest(types.CONTACT_LISTS_UPDATE_CATEGORY_ATTEMPT, updateCategory),
      takeLatest(types.CONTACT_LISTS_DELETE_CATEGORIES_ATTEMPT, deleteCategories),
      takeLatest(types.CONTACT_LISTS_REQUERY_LISTS_ATTEMPT, requeryLists),
      takeLatest(types.CONTACT_LISTS_REFRESH_COUNTS, refreshCounts),
      takeLatest(types.CONTACT_LISTS_ADD_LIST_ATTEMPT, addList),
      takeLatest(types.CONTACT_LISTS_UPDATE_LIST_ATTEMPT, updateList),
      takeLatest(types.CONTACT_LISTS_DELETE_LIST_ATTEMPT, deleteList),
      takeLatest(types.CONTACT_LISTS_DORMANT_CONTACTS_ATTEMPT, dormantContacts),
      takeLatest(types.CONTACT_LISTS_LOAD_LIST_ATTEMPT, loadContactList),
      takeLatest(types.CONTACT_LISTS_SELECT_CATEGORY, reloadContactListCategories),

      takeLatest(types.CONTACT_LISTS_UPDATE_HISTORY_STATS, updateHistoryStats),
      takeLatest(types.DIARY_ADD_ACTIVITY_SUCCESS, updateContactHistoryStatForActivity),
      takeLatest(types.DIARY_EDIT_ACTIVITY_SUCCESS, updateContactHistoryStatForActivity),
      takeLatest(types.CONTACTS_CONTACT_EDIT_SUCCESS, updateContactHistoryStatForContact),
      takeLatest(types.CONTACTS_CONTACT_DORMANT_SUCCESS, updateContactHistoryStatForContact),
      takeLatest(types.CONTACTS_CONTACT_LIVE_SUCCESS, updateContactHistoryStatForContact),
      takeLatest(types.CONTACTS_COMPANY_EDIT_SUCCESS, updateContactHistoryStatForCompany),
      takeLatest(types.CONTACTS_COMPANY_DORMANT_SUCCESS, updateContactHistoryStatForCompany),

      takeLatest(types.CONTACT_LISTS_NEW_BUILDER_LIST, newBuilderList),
      takeLatest(types.CONTACT_LISTS_GET_TOTAL_ATTEMPT, getTotal),
      takeLatest(types.CONTACT_LISTS_LOAD_LIST_ATTEMPT, getTotal),
      takeLatest(types.CONTACT_LISTS_NEW_BUILDER_LIST, getTotal),
      takeLatest(types.CONTACT_LISTS_BUILDER_REQUERY_ATTEMPT, builderRequeryList),
      takeLatest(types.CONTACT_LISTS_UPDATE_BUILDER_LIST, builderListUpdated),
      takeLatest(types.CONTACT_LIST_BUILDER_CREATE_FILTER, builderCreateFilter),
      takeLatest(types.CONTACT_LIST_BUILDER_CREATE_SUB_FILTER, builderCreateSubFilter),
      takeLatest(types.CONTACT_LIST_BUILDER_ADD_FILTER, builderFiltersChanged),
      takeLatest(types.CONTACT_LIST_BUILDER_UPDATE_FILTER, builderFiltersChanged),
      takeLatest(types.CONTACT_LIST_BUILDER_UPDATE_FILTERS, builderFiltersChanged),
      takeLatest(types.CONTACT_LIST_BUILDER_REMOVE_FILTER, builderFiltersChanged),
      takeLatest(types.CONTACT_LISTS_LOAD_FILTER_VALUES_ATTEMPT, loadFilterValues),
      takeLatest(types.ALLOCATE_CONTACT_LIST_DIALOG_ALLOCATE_CONTACT_LIST_ATTEMPT, allocateContactList),
      takeLatest(types.ALLOCATE_CONTACT_LIST_DIALOG_ALLOCATE_CONTACT_LIST_FAILURE, actionFailure),
    ])
  }

  const contactsPivotTable = createPivotTableWatcher(
      api.contact,
      listBuilderContactsPivotTableKey,
      [],
      state => {
        const {selected} = state.contactsContext.listBuilder
        if (selected) return {contactList: selected, dormants: true}

        // Shouldn't happen, but don't load anything if it does
        return {contactListId: -1}
      },
      contactFields,
      saveContactChanges
  )

  function* watcher() {
    yield fork(myWatcher)
    yield fork(contactsPivotTable)
  }

  return {
    watcher,
  }
}
